import {
    Box,
    Grid,
    Stack,
    Typography,
    Chip,
    Collapse,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Divider,
    Card,
    IconButton,
    Button,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { getRelated, statusColor } from './utils';
import { CarePlanEnum } from '../../../Enum/CarePlanEnum';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { GOAL_STATUSES } from './constants';
import { isValid, isBefore } from 'date-fns';
import { toDate } from '../../../utils/dateHelpers';
import { handleEdit, saveDragAndDrop, setDraggedItem, setIntegratedCarePlan, setProgressModal } from '../../../store/carePlanUpdate.slice';
import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { confettiShapes, confettiColors, confettiKeyframes, getThemeColor } from './carePlanAnimations';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TimeIntervalTypeEnum } from '../../../Enum/TimeIntervalTypeEnum';
import { useParams } from 'react-router-dom';
import { copyObject } from '../../../utils/common';
import Authorization from '../../../utils/Authorization';
import { CreatedBy } from '../../../Models/Shared/CreatedBy.model';

const GoalsColumn: React.FC<{ isAssessment?: boolean }> = ({ isAssessment }) => {
    const authData = new Authorization();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const params = useParams();
    const memberId = params.memberId || '';
    const { draggedItem, goalStatusTab, integratedCarePlan } = useSelector((state: RootState) => state.carePlanUpdate);
    const [goalExpanded, setGoalExpanded] = useState<Record<string, boolean>>({});

    const getCarePlanItemById = (id: string, carePlan: any) => {
        const allItems = [
            ...carePlan.Goals,
            ...carePlan.Strengths,
            ...carePlan.Barriers,
            ...carePlan.NextSteps,
            ...carePlan.Plans,
            ...carePlan.Metrics,
            ...carePlan.SchedulePlans,
        ];
        return allItems.find((item: any) => item.Id === id);
    };

    const allowDrop = (e: React.DragEvent) => e.preventDefault();
    // Drop handler for Goals card

    const handleDropOnGoal = (goalId: string) => (e: React.DragEvent) => {
        e.preventDefault();
        if (!draggedItem) return;
        // Only allow linking valid item types
        let linkType = draggedItem.type;
        if (!['strength', 'barrier', 'nextStep', 'plan', 'metric', 'schedulePlan'].includes(linkType)) return;

        const goals = integratedCarePlan.Goals.map((g) => {
            if (g.Id === goalId) {
                // Link the dragged item to the goal
                return { ...g, LinkedItems: [...(g.LinkedItems || []), draggedItem.Id] };
            }
            return g;
        });
        let updatedBy = new CreatedBy();
        updatedBy.Id = authData.UserId;
        updatedBy.Name = authData.UserName;
        const updatedGoal = goals.find((g) => g.Id === goalId);
        dispatch(saveDragAndDrop(memberId, updatedGoal, updatedBy));
        dispatch(setDraggedItem(null));
    };

    const handleRemoveLink = (goalId: string, itemId: string) => {
        const goals = integratedCarePlan.Goals.map((g) => {
            if (g.Id === goalId) {
                return { ...g, LinkedItems: g.LinkedItems?.filter((id) => id !== itemId) };
            }
            return g;
        });
        let updatedBy = new CreatedBy();
        updatedBy.Id = authData.UserId;
        updatedBy.Name = authData.UserName;
        const updatedGoal = goals.find((g) => g.Id === goalId);
        dispatch(saveDragAndDrop(memberId, updatedGoal, updatedBy));
        dispatch(setDraggedItem(null));
    };

    const handleReviewSignChange = (goalId: string, checked: boolean) => {
        const copy = copyObject(integratedCarePlan);
        const goal = copy.Goals.find((g) => g.Id === goalId);
        if (goal) {
            goal.IsSelected = checked;
            const linkedItems = goal.LinkedItems || [];
            linkedItems.forEach((itemId) => {
                const item = getCarePlanItemById(itemId, copy);
                if (item) {
                    item.IsSelected = checked;
                }
            });
        }
        dispatch(setIntegratedCarePlan(copy));
    };

    return (
        <Box
            sx={{
                width: '100%',
                mx: 'auto',
                pr: 1,
            }}
        >
            {(() => {
                // Filter by deleted and by selected status tab
                const filteredGoals = integratedCarePlan.Goals.filter((g) => g.Status === goalStatusTab);
                return filteredGoals.length > 0 ? (
                    filteredGoals.map((g, index) => {
                        const isAchieved = g.Status === CarePlanEnum.Achieved;
                        const relatedStrengths = getRelated(g, 'strength', integratedCarePlan);
                        const relatedBarriers = getRelated(g, 'barrier', integratedCarePlan);
                        const relatedNextSteps = getRelated(g, 'nextStep', integratedCarePlan);
                        const relatedPlans = getRelated(g, 'plan', integratedCarePlan);
                        const relatedMetrics = getRelated(g, 'metric', integratedCarePlan);
                        const relatedSchedulePlans = getRelated(g, 'schedulePlan', integratedCarePlan);
                        return (
                            <Card
                                key={g.Id}
                                elevation={3}
                                sx={{
                                    p: 2,
                                    pb: 1,
                                    borderRadius: 3,
                                    opacity: g.Status === CarePlanEnum.Deleted ? 0.7 : 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: isAchieved ? '2.5px solid #43a047' : g.Status === CarePlanEnum.Deleted ? '1px solid #e0e0e0' : '1px solid #e3e6f0',
                                    bgcolor: isAchieved
                                        ? 'linear-gradient(135deg,#e8f5e9 0%,#b9f6ca 100%)'
                                        : g.Status === CarePlanEnum.Deleted
                                            ? '#f0f0f0'
                                            : '#fff',
                                    boxShadow: isAchieved ? '0 8px 32px rgba(67,160,71,0.08)' : g.Status === CarePlanEnum.Deleted ? 1 : 3,
                                    position: 'relative',
                                    mb: 2,
                                }}
                                onDrop={handleDropOnGoal(g.Id)}
                                onDragOver={allowDrop}
                            >
                                {/* Confetti animation for achieved goals - smoother, leaf-like paper */}
                                {isAchieved && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            pointerEvents: 'none',
                                            zIndex: 10,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {[...Array(18)].map((_, i) => {
                                            const shape = confettiShapes[i % confettiShapes.length];
                                            const baseRotate = Math.random() * 360;
                                            const left = `${Math.random() * 90 + 5}%`;
                                            const delay = i * 0.22;
                                            const colorKey = confettiColors[i % confettiColors.length];
                                            const color = getThemeColor(theme, colorKey);
                                            return (
                                                <Box
                                                    key={i}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '-32px',
                                                        left,
                                                        width: 18,
                                                        height: 12,
                                                        backgroundColor: color,
                                                        clipPath: shape,
                                                        opacity: 0,
                                                        transform: `rotate(${baseRotate}deg)`,
                                                        animation: `confettiDropLeaf 3.2s cubic-bezier(.42,1,.62,.98) ${delay + 0.8}s forwards`,
                                                        ...confettiKeyframes,
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                                <Stack direction="column" alignItems="flex-start" spacing={1}>
                                    <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} sx={{ width: '100%' }}>
                                        <Stack direction={'row'} spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                            <Chip
                                                label={GOAL_STATUSES.find((status) => status.value === g.Status)?.label || ' - '}
                                                color={statusColor(g.Status)}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    mt: 0.5,
                                                    bgcolor: isAchieved ? theme.palette.success.main : undefined,
                                                    color: isAchieved ? theme.palette.common.white : undefined,
                                                    boxShadow: isAchieved ? `0 2px 8px ${theme.palette.success.main}55` : undefined,
                                                    border: isAchieved ? `2px solid ${theme.palette.success.light}` : undefined,
                                                    fontSize: isAchieved ? 16 : 14,
                                                    px: isAchieved ? 2 : 1,
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    animation: isAchieved ? 'chipCelebrate 1.1s cubic-bezier(.42,1.56,.62,.98)' : undefined,
                                                    ...confettiKeyframes,
                                                    '&::after': isAchieved && {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        pointerEvents: 'none',
                                                        background:
                                                            'linear-gradient(120deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.0) 35%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.0) 65%, rgba(255,255,255,0.0) 100%)',
                                                        transform: 'translateX(-100%)',
                                                        animation: 'shimmerMove 2.8s 1.1s cubic-bezier(.42,1.56,.62,.98) infinite',
                                                    },
                                                }}
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {isAssessment && (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            color="primary"
                                                            checked={g.IsSelected || false}
                                                            onChange={(_event, _checked) => {
                                                                handleReviewSignChange(g.Id, _checked);
                                                            }}
                                                            sx={{ p: 0, mx: 1 }}
                                                        />
                                                    }
                                                    label={<Typography color="primary">Select</Typography>}
                                                    sx={{ mr: 2, ml: 0, userSelect: 'none' }}
                                                />
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={() => dispatch(handleEdit(g, 'goal'))}
                                                sx={{ ml: 1 }}
                                                disabled={g.Status === CarePlanEnum.Deleted}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                    <Typography
                                        variant="h6"
                                        fontWeight={isAchieved ? 900 : 700}
                                        sx={{
                                            lineHeight: 1.5,
                                            color: isAchieved ? '#388e3c' : '#0f172a',
                                            fontSize: isAchieved ? 24 : 20,
                                            letterSpacing: isAchieved ? 1.2 : 0,
                                            transition: 'color 0.3s',
                                        }}
                                    >
                                        {g.Name}
                                        {isAchieved && (
                                            <span
                                                style={{
                                                    marginLeft: 8,
                                                    fontSize: 20,
                                                    verticalAlign: 'middle',
                                                    color: theme.palette.success.main,
                                                    display: 'inline-block',
                                                    animation: 'partyPopperPop 0.8s cubic-bezier(.42,1.56,.62,.98) forwards',
                                                }}
                                            >
                                                🎉
                                            </span>
                                        )}
                                    </Typography>
                                    <Stack direction="row" spacing={2} alignItems={'center'} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
                                        <Typography
                                            variant="body2"
                                            color={isBefore(toDate(g.DueDate), new Date()) && g.Status !== CarePlanEnum.Achieved ? 'error.main' : 'text.secondary'}
                                            sx={{ fontWeight: isAchieved ? 700 : 400 }}
                                        >
                                            <strong>Due:</strong> {g.DueDate ? new Date(g.DueDate).toLocaleDateString() : '—'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Updated At:</strong> {g.UpdatedAt ? new Date(g.UpdatedAt).toLocaleDateString() : '—'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: isAchieved ? 700 : 400 }}>
                                            <strong>Updated By:</strong> {g.UpdatedProviderById?.Name || '—'}
                                        </Typography>
                                        {isAchieved && (g as any)?.AchievedOn && (
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label={`Achieved on ${new Date((g as any).AchievedOn).toLocaleDateString()}`}
                                                sx={{
                                                    bgcolor: theme.palette.success.light,
                                                    color: '#fff',
                                                    border: `2px solid ${theme.palette.success.main}`,
                                                    fontWeight: 800,
                                                    ml: 2,
                                                    animation: 'achievePulse 1.2s cubic-bezier(.42,1.56,.62,.98) 0.6s forwards',
                                                    ...confettiKeyframes,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                </Stack>

                                <Collapse in={!!goalExpanded[g.Id]} timeout="auto" unmountOnExit>
                                    <Divider sx={{ my: 2 }} />
                                    <Box>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} color="default">
                                                    Goal Progress Notes
                                                </Typography>
                                                <Typography variant="caption" color="primary.main">
                                                    * Member added entry
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Add Progress Entry" arrow>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() =>
                                                        dispatch(
                                                            setProgressModal({
                                                                open: true,
                                                                goalId: g.Id,
                                                                progressId: null,
                                                                value: '',
                                                                type: 'goal',
                                                            })
                                                        )
                                                    }
                                                >
                                                    <Add color="primary" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                        <Box
                                            sx={{
                                                mb: 1,
                                                bgcolor: '#f7f7fa',
                                                border: '1px dashed #bdbdbd',
                                                borderRadius: 1,
                                                px: 2,
                                            }}
                                        >
                                            {g.Progress && g.Progress.length > 0 ? (
                                                <List dense sx={{ pl: 1 }}>
                                                    {g.Progress.map((p: any, idx: number) => {
                                                        const memberEntry = p.CreatedBy?.Id === memberId;
                                                        return (
                                                            <ListItem
                                                                key={p.Id}
                                                                sx={{
                                                                    pl: 0,
                                                                    borderBottom: idx !== g.Progress.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                                    py: 1,
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={<span style={{ fontSize: 13 }}>{p.Name}</span>}
                                                                    secondary={
                                                                        <span
                                                                            style={{
                                                                                fontSize: 12,
                                                                                fontStyle: 'italic',
                                                                                color: memberEntry ? '#1976d2' : '',
                                                                            }}
                                                                        >{`${memberEntry ? ' * ' : ''}${new Date(p.CreatedAt).toLocaleString()} ${p.CreatedBy?.Name ? `by ${p.CreatedBy.Name}` : ''
                                                                            }`}</span>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            ) : (
                                                <Typography color="text.secondary" fontStyle="italic" sx={{ mb: 0, py: 2, fontSize: 13 }}>
                                                    No progress entries.
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Grid container spacing={2} my={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            {/* Strengths Section */}
                                            <Typography variant="subtitle2" color="success.main" fontWeight={600} sx={{ mb: 0.5 }}>
                                                Strengths
                                            </Typography>
                                            <Box
                                                sx={{
                                                    bgcolor: relatedStrengths.length > 0 ? '#f6fbf7' : 'transparent',
                                                    border: relatedStrengths.length > 0 ? '1px solid #c8e6c9' : 'none',
                                                    borderRadius: 2,
                                                    px: relatedStrengths.length > 0 ? 1 : 0,
                                                    ml: 5,
                                                }}
                                            >
                                                {relatedStrengths.length > 0 ? (
                                                    <List disablePadding>
                                                        {relatedStrengths.map((s, idx) => (
                                                            <React.Fragment key={s.Id}>
                                                                <ListItem
                                                                    sx={{
                                                                        borderBottom: idx !== relatedStrengths.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                                        py: 1,
                                                                    }}
                                                                    secondaryAction={
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            onClick={() => handleRemoveLink(g.Id, s.Id)}
                                                                            disabled={g.Status === CarePlanEnum.Deleted}
                                                                        >
                                                                            <Delete fontSize="small" />
                                                                        </IconButton>
                                                                    }
                                                                >
                                                                    <ListItemText primary={s.Name} />
                                                                </ListItem>
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" fontStyle="italic" sx={{ mb: 0 }}>
                                                        No strengths linked.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            {/* Barriers Section */}
                                            <Typography variant="subtitle2" color="error.main" fontWeight={600} sx={{ mb: 0.5 }}>
                                                Barriers
                                            </Typography>
                                            <Box
                                                sx={{
                                                    bgcolor: relatedBarriers.length > 0 ? '#fff6f6' : 'transparent',
                                                    border: relatedBarriers.length > 0 ? '1px solid #ffcdd2' : 'none',
                                                    borderRadius: 2,
                                                    px: relatedBarriers.length > 0 ? 1 : 0,
                                                    ml: 5,
                                                }}
                                            >
                                                {relatedBarriers.length > 0 ? (
                                                    <List disablePadding>
                                                        {relatedBarriers.map((b, idx) => (
                                                            <React.Fragment key={b.Id}>
                                                                <ListItem
                                                                    sx={{
                                                                        borderBottom: idx !== relatedBarriers.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                                        py: 1,
                                                                    }}
                                                                    secondaryAction={
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            onClick={() => handleRemoveLink(g.Id, b.Id)}
                                                                            disabled={g.Status === CarePlanEnum.Deleted}
                                                                        >
                                                                            <Delete fontSize="small" />
                                                                        </IconButton>
                                                                    }
                                                                >
                                                                    <ListItemText primary={b.Name} />
                                                                </ListItem>
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" fontStyle="italic" sx={{ mb: 0 }}>
                                                        No barriers linked.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            {/* Plans Section */}
                                            <Typography variant="subtitle2" color="info.dark" fontWeight={600} sx={{ mb: 0.5 }}>
                                                Plans
                                            </Typography>
                                            <Box
                                                sx={{
                                                    bgcolor: relatedPlans.length > 0 ? '#f3f7fa' : 'transparent',
                                                    border: relatedPlans.length > 0 ? '1px solid #b3e5fc' : 'none',
                                                    borderRadius: 2,
                                                    px: relatedPlans.length > 0 ? 1.5 : 0,
                                                    ml: 5,
                                                }}
                                            >
                                                {relatedPlans.length > 0 ? (
                                                    <List disablePadding>
                                                        {relatedPlans.map((p, idx) => (
                                                            <React.Fragment key={p.Id}>
                                                                <ListItem
                                                                    sx={{
                                                                        borderBottom: idx !== relatedPlans.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                                        py: 1,
                                                                    }}
                                                                    secondaryAction={
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            onClick={() => handleRemoveLink(g.Id, p.Id)}
                                                                            disabled={g.Status === CarePlanEnum.Deleted}
                                                                        >
                                                                            <Delete fontSize="small" />
                                                                        </IconButton>
                                                                    }
                                                                >
                                                                    <ListItemText primary={p.Name} />
                                                                </ListItem>
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" fontStyle="italic" sx={{ mb: 0 }}>
                                                        No plans linked.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            {/* Next Steps Section */}
                                            <Typography variant="subtitle2" color="secondary.main" fontWeight={600} sx={{ mb: 0.5 }}>
                                                Next Steps
                                            </Typography>
                                            <Box
                                                sx={{
                                                    bgcolor: relatedNextSteps.length > 0 ? '#f7f3fa' : 'transparent',
                                                    border: relatedNextSteps.length > 0 ? '1px solid #d1c4e9' : 'none',
                                                    borderRadius: 2,
                                                    px: relatedNextSteps.length > 0 ? 1.5 : 0,
                                                    ml: 5,
                                                }}
                                            >
                                                {relatedNextSteps.length > 0 ? (
                                                    <List disablePadding>
                                                        {relatedNextSteps.map((m, idx) => (
                                                            <React.Fragment key={m.Id}>
                                                                <ListItem
                                                                    sx={{
                                                                        borderBottom: idx !== relatedNextSteps.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                                        py: 1,
                                                                    }}
                                                                    secondaryAction={
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            onClick={() => handleRemoveLink(g.Id, m.Id)}
                                                                            disabled={g.Status === CarePlanEnum.Deleted}
                                                                        >
                                                                            <Delete fontSize="small" />
                                                                        </IconButton>
                                                                    }
                                                                >
                                                                    <ListItemText primary={m.Name} />
                                                                </ListItem>
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" fontStyle="italic" sx={{ mb: 0 }}>
                                                        No next steps linked.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            {/* Metrics Section */}
                                            <Typography variant="subtitle2" color="info.light" fontWeight={600} sx={{ mb: 0.5 }}>
                                                Metrics
                                            </Typography>
                                            <Box
                                                sx={{
                                                    bgcolor: relatedMetrics.length > 0 ? '#f3f7fa' : 'transparent',
                                                    border: relatedMetrics.length > 0 ? '1px solid #b3e5fc' : 'none',
                                                    borderRadius: 2,
                                                    px: relatedMetrics.length > 0 ? 1.5 : 0,
                                                    ml: 5,
                                                }}
                                            >
                                                {relatedMetrics.length > 0 ? (
                                                    <List>
                                                        {relatedMetrics.map((m, idx) => (
                                                            <React.Fragment key={m.Id}>
                                                                <ListItem
                                                                    sx={{
                                                                        borderBottom: idx !== relatedMetrics.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                                        py: 1,
                                                                    }}
                                                                    secondaryAction={
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            onClick={() => handleRemoveLink(g.Id, m.Id)}
                                                                            disabled={g.Status === CarePlanEnum.Deleted}
                                                                        >
                                                                            <Delete fontSize="small" />
                                                                        </IconButton>
                                                                    }
                                                                >
                                                                    <ListItemText primary={m.Name} />
                                                                </ListItem>
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" fontStyle="italic" sx={{ mb: 0 }}>
                                                        No metrics linked.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            {/* Schedule Plans Section */}
                                            <Typography variant="subtitle2" color="warning.main" fontWeight={600} sx={{ mb: 0.5 }}>
                                                Schedule Plans
                                            </Typography>
                                            <Box
                                                sx={{
                                                    bgcolor: relatedSchedulePlans.length > 0 ? '#fff8e1' : 'transparent',
                                                    border: relatedSchedulePlans.length > 0 ? '1px solid #ffe0b2' : 'none',
                                                    borderRadius: 2,
                                                    px: relatedSchedulePlans.length > 0 ? 1.5 : 0,
                                                    ml: 5,
                                                }}
                                            >
                                                {relatedSchedulePlans.length > 0 ? (
                                                    <List disablePadding>
                                                        {relatedSchedulePlans.map((sp, idx) => (
                                                            <React.Fragment key={sp.Id}>
                                                                <ListItem
                                                                    sx={{
                                                                        borderBottom: idx !== relatedSchedulePlans.length - 1 ? '1px solid #e0e0e0' : 'none',
                                                                        py: 1,
                                                                    }}
                                                                    secondaryAction={
                                                                        <IconButton
                                                                            edge="end"
                                                                            size="small"
                                                                            onClick={() => handleRemoveLink(g.Id, sp.Id)}
                                                                            disabled={g.Status === CarePlanEnum.Deleted}
                                                                        >
                                                                            <Delete fontSize="small" />
                                                                        </IconButton>
                                                                    }
                                                                >
                                                                    <ListItemText
                                                                        primary={`Appointment with ${sp.Provider?.Role || '—'} occurs every ${sp.TimeIntervalIndays
                                                                            } ${TimeIntervalTypeEnum[sp.TimeIntervalType]}`}
                                                                    />
                                                                </ListItem>
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                ) : (
                                                    <Typography color="text.secondary" fontStyle="italic" sx={{ mb: 0 }}>
                                                        No schedule plans linked.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Collapse>
                                <Button
                                    size="small"
                                    variant="text"
                                    color="primary"
                                    sx={{ height: 28, py: 2.5 }}
                                    onClick={() => setGoalExpanded((prev) => ({ ...prev, [g.Id]: !prev[g.Id] }))}
                                >
                                    {goalExpanded[g.Id] ? 'Hide Details' : 'Show Details'}
                                </Button>
                            </Card>
                        );
                    })
                ) : (
                    <Typography color="text.secondary">No goals available.</Typography>
                );
            })()}
        </Box>
    );
};

export default GoalsColumn;
